import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { useSEO } from "@/components/SEO";
import { Eyebrow, Masked, Reveal } from "@/components/bits";
import FAQ from "@/components/FAQ";
import { SEO } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCUSS = ["Growth objectives", "Revenue model", "Sales organization", "Average transaction value", "Pipeline", "Marketing investment", "CRM / customer database", "Sales cycle", "Dormant opportunities", "Conversion bottlenecks", "Fulfillment capacity", "Margin pressure", "Vendor / operating-cost categories"];

const REVENUE_OPTS = ["Under $30M", "$30M–$50M", "$50M–$100M", "$100M–$150M", "$150M+"];
const SALES_OPTS = ["1–5", "6–15", "16–30", "31–75", "75+"];

const empty = { first_name: "", last_name: "", email: "", phone: "", company: "", website: "", job_title: "", annual_revenue: "", salespeople: "", challenge: "" };

const inputCls = "w-full rounded-lg border border-ink/15 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink/35 outline-none transition-colors duration-200 focus:border-vantera";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-slategray";

const Field = ({ id, label, required, error, ...rest }) => (
    <div>
        <label htmlFor={id} className={labelCls}>
            {label} {required && <span className="text-vantera">*</span>}
        </label>
        <input id={id} data-testid={`form-${id}`} className={`${inputCls} ${error ? "border-rose-400" : ""}`} {...rest} />
        {error && <p className="mt-1.5 text-[12px] text-rose-500" data-testid={`form-error-${id}`}>{error}</p>}
    </div>
);

const Contact = () => {
    useSEO({ title: SEO["/contact"].title, description: SEO["/contact"].description, path: "/contact" });
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(empty);
    const [errors, setErrors] = useState({});
    const [state, setState] = useState("idle");
    const [serverError, setServerError] = useState("");

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const validate = (s) => {
        const e = {};
        if (s === 0) {
            if (!form.first_name.trim()) e.first_name = "Required";
            if (!form.last_name.trim()) e.last_name = "Required";
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid work email";
        }
        if (s === 1 && !form.company.trim()) e.company = "Required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const next = () => {
        if (validate(step)) setStep(step + 1);
    };

    const submit = async () => {
        if (!validate(0) || !validate(1)) return;
        if (!form.challenge.trim()) {
            setErrors({ challenge: "A sentence or two helps us prepare" });
            return;
        }
        setState("sending");
        try {
            await axios.post(`${API}/contact`, form);
            setState("sent");
        } catch (err) {
            setState("idle");
            setServerError("Something went wrong submitting the form. Please try again.");
        }
    };

    return (
        <>
            <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20" data-testid="contact-hero">
                <div className="bg-grid-light absolute inset-0 [mask-image:radial-gradient(70%_60%_at_35%_20%,black,transparent)]" aria-hidden="true" />
                <div className="container-x relative">
                    <Eyebrow>Contact — Executive Review</Eyebrow>
                    <Masked
                        lines={["Find Out Where Your Next", "Profit Opportunity Is Hiding."]}
                        className="mt-7 max-w-5xl font-display text-4xl font-extrabold leading-[1.06] tracking-tightest sm:text-6xl lg:text-[68px]"
                    />
                    <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slategray sm:text-xl">
                        The opportunity may be in acquisition, follow-up, dormant accounts, advertising, sales productivity, margin, or unnecessary operating expense. The first step is understanding where the highest-value opportunities exist.
                    </p>
                </div>
            </section>

            <section className="pb-24 sm:pb-32" data-testid="contact-main">
                <div className="container-x grid gap-14 lg:grid-cols-2 lg:gap-20">
                    <div>
                        <Eyebrow>Initial Executive Review</Eyebrow>
                        <Masked inview lines={["A Working Conversation,", "Not a Sales Pitch."]} className="mt-7 font-display text-3xl font-extrabold tracking-tightest sm:text-4xl" />
                        <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-slategray">
                            In the Executive Review, we discuss:
                        </p>
                        <ul className="mt-7 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                            {DISCUSS.map((d, i) => (
                                <Reveal key={d} delay={i * 0.03}>
                                    <li className="flex items-center gap-3 text-[15.5px] font-medium text-ink">
                                        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-vantera-soft text-vantera" aria-hidden="true">
                                            <Check size={10} strokeWidth={3.5} />
                                        </span>
                                        {d}
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                        <p className="mt-8 border-l-2 border-vantera pl-5 text-[16.5px] leading-relaxed text-slategray">
                            If there appears to be a meaningful economic opportunity, the next step is the Business X-Ray.
                        </p>
                    </div>

                    <div className="lg:sticky lg:top-24 lg:self-start">
                        <div className="scan-sweep relative overflow-hidden rounded-2xl border border-ink/10 bg-white p-7 sm:p-9" data-testid="executive-review-form">
                            <AnimatePresence mode="wait">
                                {state === "sent" ? (
                                    <motion.div key="done" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="py-14 text-center" data-testid="form-success">
                                        <span className="mx-auto flex size-16 items-center justify-center rounded-full bg-vantera-soft text-vantera" aria-hidden="true">
                                            <Check size={28} strokeWidth={2.5} />
                                        </span>
                                        <h3 className="mt-7 font-display text-2xl font-extrabold tracking-tight">Request received.</h3>
                                        <p className="mx-auto mt-3 max-w-sm text-[16px] leading-relaxed text-slategray">
                                            Thank you, {form.first_name}. Your Executive Review request has been logged, and Vantera will respond at {form.email}.
                                        </p>
                                    </motion.div>
                                ) : (
                                    <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        <div className="flex items-center justify-between">
                                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slategray">Request my executive review</p>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-vantera">Step {step + 1} / 3</p>
                                        </div>
                                        <div className="mt-4 h-[3px] w-full overflow-hidden rounded-full bg-ink/[0.07]">
                                            <motion.div className="h-full rounded-full bg-vantera" animate={{ width: `${((step + 1) / 3) * 100}%` }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} data-testid="form-progress" />
                                        </div>

                                        <div className="mt-8 space-y-5">
                                            {step === 0 && (
                                                <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="space-y-5" data-testid="form-step-1">
                                                    <div className="grid gap-5 sm:grid-cols-2">
                                                        <Field id="first-name" label="First Name" required value={form.first_name} onChange={set("first_name")} error={errors.first_name} autoComplete="given-name" />
                                                        <Field id="last-name" label="Last Name" required value={form.last_name} onChange={set("last_name")} error={errors.last_name} autoComplete="family-name" />
                                                    </div>
                                                    <Field id="email" label="Work Email" required type="email" value={form.email} onChange={set("email")} error={errors.email} autoComplete="email" />
                                                    <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
                                                </motion.div>
                                            )}
                                            {step === 1 && (
                                                <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} className="space-y-5" data-testid="form-step-2">
                                                    <div className="grid gap-5 sm:grid-cols-2">
                                                        <Field id="company" label="Company" required value={form.company} onChange={set("company")} error={errors.company} autoComplete="organization" />
                                                        <Field id="website" label="Company Website" value={form.website} onChange={set("website")} placeholder="company.com" autoComplete="url" />
                                                    </div>
                                                    <Field id="job-title" label="Job Title" value={form.job_title} onChange={set("job_title")} autoComplete="organization-title" />
                                                    <div className="grid gap-5 sm:grid-cols-2">
                                                        <div>
                                                            <label htmlFor="revenue" className={labelCls}>Approximate Annual Revenue</label>
                                                            <select id="revenue" data-testid="form-revenue" className={inputCls} value={form.annual_revenue} onChange={set("annual_revenue")}>
                                                                <option value="">Select</option>
                                                                {REVENUE_OPTS.map((o) => <option key={o}>{o}</option>)}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label htmlFor="salespeople" className={labelCls}>Number of Salespeople</label>
                                                            <select id="salespeople" data-testid="form-salespeople" className={inputCls} value={form.salespeople} onChange={set("salespeople")}>
                                                                <option value="">Select</option>
                                                                {SALES_OPTS.map((o) => <option key={o}>{o}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                            {step === 2 && (
                                                <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} data-testid="form-step-3">
                                                    <label htmlFor="challenge" className={labelCls}>
                                                        Biggest revenue-growth or cost-efficiency challenge <span className="text-vantera">*</span>
                                                    </label>
                                                    <textarea id="challenge" data-testid="form-challenge" rows="5" className={`${inputCls} resize-none ${errors.challenge ? "border-rose-400" : ""}`} value={form.challenge} onChange={set("challenge")} placeholder="Where do you suspect revenue is leaking, or cost is higher than it should be?" />
                                                    {errors.challenge && <p className="mt-1.5 text-[12px] text-rose-500" data-testid="form-error-challenge">{errors.challenge}</p>}
                                                </motion.div>
                                            )}
                                        </div>

                                        {serverError && <p className="mt-4 text-[13px] text-rose-500" data-testid="form-server-error">{serverError}</p>}

                                        <div className="mt-8 flex items-center justify-between gap-4">
                                            {step > 0 ? (
                                                <button onClick={() => setStep(step - 1)} data-testid="form-back" className="font-display text-[14px] font-bold text-slategray transition-colors hover:text-ink">← Back</button>
                                            ) : <span />}
                                            {step < 2 ? (
                                                <button onClick={next} data-testid="form-next" className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3 font-display text-[14px] font-bold text-ivory transition-colors duration-300 hover:bg-vantera hover:text-ink">
                                                    Continue <span aria-hidden="true">→</span>
                                                </button>
                                            ) : (
                                                <button onClick={submit} disabled={state === "sending"} data-testid="form-submit" className="inline-flex items-center gap-2 rounded-full bg-vantera px-7 py-3 font-display text-[14px] font-bold text-ink transition-colors duration-300 hover:bg-ink hover:text-ivory disabled:opacity-60">
                                                    {state === "sending" ? "Submitting…" : "Request My Executive Review"}
                                                </button>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-t border-ink/10 bg-paper py-24 sm:py-32" data-testid="contact-faq">
                <div className="container-x grid gap-12 lg:grid-cols-[0.7fr_1.3fr]">
                    <div className="lg:sticky lg:top-28 lg:self-start">
                        <Eyebrow>Common questions</Eyebrow>
                        <Masked inview lines={["Before You Ask."]} className="mt-7 font-display text-4xl font-extrabold tracking-tightest sm:text-5xl" />
                    </div>
                    <FAQ />
                </div>
            </section>
        </>
    );
};

export default Contact;